import React, { useState, useRef } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Register Chart.js components (required for react-chartjs-2)
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

import AILightComponent from '../../components/AILightComponent';

// Placeholder for your existing MyAIInsight component
// If you haven't created it yet, replace the modal below with <MyAIInsight data={currentData} onClose={() => setShowAI(false)} />
const MyAIInsight = ({ data, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2">
      <div className="bg-zinc-900 border border-white/20 rounded-md w-full max-h-[90vh] overflow-auto p-2 text-white">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg lg:text-xl font-bold text-cyan-400">AI Insights for {data?.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-100 hover:text-white w-12 text-xl lg:text-2xl leading-none"
          >
            ×
          </button>
        </div>
        {/* <AILightComponent param1={notesType} param2={' Notes/Comments/Remarks Title: ' + userCommentTitle + ', Details: ' + userCommentDetails} /> */}
        <div className="space-y-6 text-white/90">
          <p className="text-lg">
            Based on the trend data you provided, here are AI-powered insights:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="bg-black/40 p-2 rounded-sm">
              <h3 className="font-semibold mb-2 text-emerald-400">Growth Pattern</h3>
              <p className="text-sm">Steady upward trajectory with {Math.round((Object.values(data?.scores || {}).reduce((a, b) => a + b, 0) / Object.keys(data?.scores || {}).length) || 0)} average score. Peak acceleration between periods {Object.keys(data?.scores || {})[2]} and {Object.keys(data?.scores || {}).slice(-2)[0]}.</p>
            </div>
            <div className="bg-black/40 p-2 rounded-sm">
              <h3 className="font-semibold mb-2 text-amber-400">Key Inflection</h3>
              <p className="text-sm">Major jump of +{Math.max(...Object.values(data?.scores || {})) - Math.min(...Object.values(data?.scores || {}))} points observed. This suggests external catalyst around the midpoint of your selected period.</p>
            </div>
          </div>
          <div className="bg-black/40 p-2 rounded-sm">
            <h3 className="font-semibold mb-3">Recommended Actions</h3>
            <ul className="space-y-3 text-sm [&_li]:text-gray-100">
              <li className="flex items-start gap-3">
                <span className="text-emerald-400 text-xl">→</span>
                <span>Monitor quarterly checkpoints — your trend shows compounding momentum.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-emerald-400 text-xl">→</span>
                <span>Consider expanding data collection to include external factors (market events, policy changes).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-400 text-xl">→</span>
                <span>Forecast suggests reaching 90+ score by {Object.keys(data?.scores || {}).slice(-1)[0]} if current velocity continues.</span>
              </li>
            </ul>
          </div>
        </div>
        <button
          onClick={onClose}
          className="mt-2 w-32 lg:w-40 bg-white text-black py-2 rounded-sm font-bold text-xs lg:text-sm hover:bg-gray-200 transition-colors"
        >
          Close Insights
        </button>
      </div>
    </div>
  );
};

const MyLifeTrends = () => {
    const authState = useSelector((state) => state.authReducer || {});
    const isLoggedIn = authState?.isLoggedIn || false;
    const userName = authState?.user?.name || "User";

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("Learning & Development");

    const handleLogout = () => {
        dispatch(logout());
        navigate("/");
    };

  const [activeMode, setActiveMode] = useState('input'); // 'input' | 'list'
  const [formData, setFormData] = useState({
    categoryType: 'Single', // Single or Multiple
    title: '',
    description: '',
    periodType: 'Year wise',
    startPeriod: '',
    endPeriod: '',
    interval: 1,
    scoresText: '',
  });

  const [myLifeTrends, setMyLifeTrends] = useState([
    {
      id: 1,
      title: 'Electric Vehicle Adoption Curve',
      dateCreated: '2025-03-20',
      fullData: {
        categoryType: 'Single',
        title: 'Electric Vehicle Adoption Curve',
        description: 'Global shift toward sustainable mobility',
        periodType: 'Year wise',
        startPeriod: '2018',
        endPeriod: '2025',
        interval: 1,
        scores: { '2018': 12, '2019': 18, '2020': 25, '2021': 38, '2022': 52, '2023': 68, '2024': 79, '2025': 88 },
      },
    },
    {
      id: 2,
      title: 'AI Integration in Customer Service',
      dateCreated: '2025-03-18',
      fullData: {
        categoryType: 'Multiple',
        title: 'AI Integration in Customer Service',
        description: 'Lifecycle of chatbot & AI agent deployment',
        periodType: 'Year wise',
        startPeriod: '2021',
        endPeriod: '2026',
        interval: 1,
        scores: { '2021': 15, '2022': 42, '2023': 67, '2024': 81, '2025': 92, '2026': 95 },
      },
    },
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showGraph, setShowGraph] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [showAI, setShowAI] = useState(false);
  const previewRef = useRef(null);

  const sampleData = {
    categoryType: 'Single',
    title: 'AI Adoption in Enterprises',
    description: 'Lifecycle trend of artificial intelligence integration across Fortune 500 companies',
    periodType: 'Year wise',
    startPeriod: '2020',
    endPeriod: '2025',
    interval: 1,
    scoresText: '"2020":20, "2021":35, "2022":45, "2023":60, "2024":75, "2025":85',
    scores: { '2020': 20, '2021': 35, '2022': 45, '2023': 60, '2024': 75, '2025': 85 },
  };

  // Generate periods based on selection
  const generatePeriods = (start, end, periodType, interval) => {
    const periods = [];
    const int = parseInt(interval);

    if (periodType === 'Year wise') {
      const s = parseInt(start);
      const e = parseInt(end);
      for (let i = s; i <= e; i += int) {
        periods.push(i.toString());
      }
    } else if (periodType === 'Monthly') {
      // Simple monthly generation assuming YYYY-MM format or year start
      let currentYear = parseInt(start);
      let currentMonth = 1;
      let count = 0;
      while (count < 60) { // safety
        periods.push(`${currentYear}-${currentMonth.toString().padStart(2, '0')}`);
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
        count++;
        if (currentYear > parseInt(end)) break;
      }
      // Slice according to interval (every N months)
      return periods.filter((_, idx) => idx % int === 0);
    } else if (periodType === 'Quarterly') {
      const s = parseInt(start);
      const e = parseInt(end);
      for (let y = s; y <= e; y++) {
        for (let q = 1; q <= 4; q += int) {
          periods.push(`${y} Q${q}`);
        }
      }
    } else if (periodType === 'Weekly') {
      periods.push('Week 1', 'Week 2', '...'); // placeholder for demo
      return Array.from({ length: 12 }, (_, i) => `Week ${i * int + 1}`);
    } else if (periodType === 'Daily') {
      periods.push('Day 1', 'Day 2', '...');
      return Array.from({ length: 10 }, (_, i) => `Day ${i * int + 1}`);
    }
    return periods;
  };

  const handleLoadSample = () => {
    setFormData({
      categoryType: sampleData.categoryType,
      title: sampleData.title,
      description: sampleData.description,
      periodType: sampleData.periodType,
      startPeriod: sampleData.startPeriod,
      endPeriod: sampleData.endPeriod,
      interval: sampleData.interval,
      scoresText: sampleData.scoresText,
    });
    setActiveMode('input');
    // Auto-generate graph for sample
    setTimeout(() => {
      handleSubmit(true); // true = isSample
    }, 300);
  };

  const handleGenerateTemplate = () => {
    if (!formData.startPeriod || !formData.endPeriod || !formData.periodType) {
      alert('Please fill Starting Period, Ending Period and Period Type first');
      return;
    }
    const periods = generatePeriods(
      formData.startPeriod,
      formData.endPeriod,
      formData.periodType,
      formData.interval
    );
    const template = periods.map((p) => `"${p}":50`).join(', ');
    setFormData((prev) => ({ ...prev, scoresText: template }));
  };

  const handleSubmit = (isSample = false) => {
    // Validation
    if (
      !formData.title ||
      !formData.description ||
      !formData.startPeriod ||
      !formData.endPeriod ||
      !formData.scoresText.trim()
    ) {
      alert('Please fill all required fields');
      return;
    }

    let scoresStr = formData.scoresText.trim();
    // Make it valid JSON if user forgot braces
    if (!scoresStr.startsWith('{')) scoresStr = `{${scoresStr}}`;
    if (!scoresStr.endsWith('}')) scoresStr += '}';

    let scores;
    try {
      scores = JSON.parse(scoresStr);
      Object.values(scores).forEach((val) => {
        if (typeof val !== 'number' || val < 1 || val > 100) {
          throw new Error('Score must be number between 1-100');
        }
      });
    } catch (e) {
      alert('Invalid score format!\n\nUse exact format:\n"1995":56, "1996":78, "1997":25\n\nKeys must be quoted, values numbers 1-100.');
      return;
    }

    const finalData = {
      ...formData,
      scores,
    };

    setCurrentData(finalData);

    // Prepare chart data
    const sortedKeys = Object.keys(scores).sort();
    const chartConfig = {
      labels: sortedKeys,
      datasets: [
        {
          label: formData.title,
          data: sortedKeys.map((key) => scores[key]),
          borderColor: '#ffffff',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderWidth: 3,
          tension: 0.3,
          pointBackgroundColor: '#ffffff',
          pointBorderColor: '#000000',
          pointRadius: 5,
        },
      ],
    };

    setGraphData(chartConfig);
    setShowGraph(true);

    // Add to My List (demo)
    if (!isSample) {
      const newTrend = {
        id: Date.now(),
        title: formData.title,
        dateCreated: new Date().toISOString().split('T')[0],
        fullData: finalData,
      };
      setMyLifeTrends((prev) => [newTrend, ...prev]);
    }

    // Scroll to graph
    setTimeout(() => {
      document.getElementById('graph-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleDownloadPDF = async () => {
    if (!previewRef.current) return;
    const canvas = await html2canvas(previewRef.current, {
      scale: 2,
      backgroundColor: '#000000',
    });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${formData.title || 'My-Trend'}.pdf`);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: formData.title,
        text: formData.description,
      });
    } else {
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard! (demo)');
      });
    }
  };

  const filteredTrends = myLifeTrends.filter((trend) =>
    trend.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#ffffff', font: { size: 12, weight: 'bold' } },
      },
      title: {
        display: true,
        text: formData.title || 'Lifecycle Trend',
        color: '#ffffff',
        font: { size: 16, weight: 'bold' },
      },
      tooltip: {
        backgroundColor: '#000000',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
      },
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.15)', lineWidth: 1 },
        ticks: { color: '#ffffff', font: { size: 11 } },
      },
      y: {
        min: 0,
        max: 100,
        grid: { color: 'rgba(255,255,255,0.15)', lineWidth: 1 },
        ticks: { color: '#ffffff', font: { size: 11 }, stepSize: 10 },
      },
    },
  };

  return (
    <div className="bg-black text-white [&_p]:text-white min-h-screen font-sans">
      <div className="border-b border-white/10 bg-black sticky top-0 z-50">
        <header className="sticky top-0 z-50 bg-black border-b border-blue-400 px-4 py-3 flex items-center justify-between">
            <div className="flex flex-col">
                <div className="text-lg lg:text-xl font-bold text-yellow-200">My Lifecycle Trends</div>
                <div className="text-[11px] text-gray-200">Think5Whys Platform • Track any trend over time!</div>
            </div>
    
            <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
                <>
                <div className="text-xs lg:text-sm text-teal-300">{userName}</div>
                <button
                    onClick={handleLogout}
                    className="w-8 mt-2 px-2 py-1 rounded-md bg-red-600 hover:bg-red-700 transition"
                    title="Logout"
                >
                    ⏻
                </button>
                </>
            ) : (
                <>
                <button
                    onClick={() => navigate("/login")}
                    className="w-8 px-2 py-1 rounded-full border border-gray-700 text-sm text-gray-200 hover:bg-gray-800"
                >
                    🔐 
                </button>
                </>
            )}
            </div>
            <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-8 h-8 flex items-center justify-center text-2xl text-teal-300"
            aria-label="Toggle menu"
            >
            {isMobileMenuOpen ? "×" : "☰"}
            </button>
        </header>
    
        {isMobileMenuOpen && (
            <div className="md:hidden bg-gray-900 border-b border-gray-800 px-4 py-3">
            {isLoggedIn ? (
                <div className="flex flex-col items-center gap-3">
                <div className="text-teal-300 text-xs lg:text-sm font-medium">{userName}</div>
                    <div className="flex gap-3">
                    <button
                        onClick={handleLogout}
                        className="p-2 rounded-md bg-red-600 hover:bg-red-700"
                    >
                        ⏻
                    </button>
                    </div>
                </div>
            ) : (
                <div className="flex justify-center gap-6">
                <Link to="/login" className="text-teal-300">🔐</Link>
                </div>
            )}
            </div>
        )}
        {/* </div> */}

        {/* Action Buttons */}
        <div className="px-2 pb-2">
          <div className="flex flex-row gap-1 justify-left">
            <button
              onClick={handleLoadSample}
              className="w-32 lg:w-40 bg-blue-500 text-white px-2 py-1 rounded-md font-bold text-xs lg:text-sm flex items-center justify-center gap-2 hover:scale-105 transition-all active:scale-95"
            >
              📊 Load Sample
            </button>
            <button
              onClick={() => setActiveMode('list')}
              className="w-24 lg:w-32 bg-blue-500 hover:bg-blue-600 text-white border border-white/30 px-2 py-1 rounded-md font-bold text-xs lg:text-sm flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              📋 My List
            </button>
            <button
              onClick={() => setActiveMode('input')}
              className="w-32 lg:w-48 bg-blue-500 hover:bg-blue-600 text-white border border-white/30 px-2 py-1 rounded-md font-bold text-xs lg:text-sm flex items-center justify-center gap-2 transition-all active:scale-95"
            >
              ✏️ My Data Input
            </button>
          </div>
        </div>
      </div>

      <div className="px-2 pb-4">
        {activeMode === 'list' && (
          <div className="mt-2">
            <div className="flex flex-col gap-2 mb-2">
              <h2 className="text-lg lg:text-xl text-yellow-200 font-semibold">My Saved Trends</h2>
              <input
                type="text"
                placeholder="Filter by title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 bg-white/10 border border-white/30 focus:border-white/60 rounded-md px-6 py-3 text-white placeholder:text-white/40 outline-none"
              />
            </div>

            <div className="flex flex-col">
              {filteredTrends.length === 0 ? (
                <div className="col-span-full text-center py-2 text-white/40">
                  No trends match your filter
                </div>
              ) : (
                filteredTrends.map((trend) => (
                  <div
                    key={trend.id}
                    onClick={() => {
                      setFormData(trend.fullData);
                      setCurrentData(trend.fullData);
                      const sortedKeys = Object.keys(trend.fullData.scores || {}).sort();
                      setGraphData({
                        labels: sortedKeys,
                        datasets: [
                          {
                            label: trend.title,
                            data: sortedKeys.map((k) => trend.fullData.scores[k]),
                            borderColor: '#ffffff',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            borderWidth: 3,
                            tension: 0.3,
                            pointBackgroundColor: '#ffffff',
                          },
                        ],
                      });
                      setShowGraph(true);
                      setActiveMode('input');
                    }}
                    className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-md p-2 cursor-pointer transition-all"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-sm lg:text-lg text-cyan-400 group-hover:text-emerald-400 transition-colors">
                          {trend.title}
                        </h3>
                        <p className="text-white/60 text-sm mt-1">
                          Created on {trend.dateCreated}
                        </p>
                      </div>
                      <div className="text-xs bg-white/10 px-3 py-1 rounded-sm font-mono">
                        {Object.keys(trend.fullData.scores || {}).length} periods
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* MODE: INPUT / DATA ENTRY */}
        {activeMode === 'input' && (
          <div className="mt-8 bg-white/5 border border-white/10 rounded-md p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
              <div className="space-y-8">
                <div>
                  <label className="text-sm font-medium text-gray-100 mb-3">Category Type</label>
                  <div className="flex rounded-md bg-white/10 p-1 w-fit">
                    <button
                      onClick={() => setFormData((p) => ({ ...p, categoryType: 'Single' }))}
                      className={`px-8 py-3 rounded-md font-medium transition-all ${
                        formData.categoryType === 'Single'
                          ? 'bg-white text-black shadow-inner'
                          : 'hover:bg-white/10'
                      }`}
                    >
                      Single
                    </button>
                    <button
                      onClick={() => setFormData((p) => ({ ...p, categoryType: 'Multiple' }))}
                      className={`px-8 py-3 rounded-md font-medium transition-all ${
                        formData.categoryType === 'Multiple'
                          ? 'bg-white text-black shadow-inner'
                          : 'hover:bg-white/10'
                      }`}
                    >
                      Multiple
                    </button>
                  </div>
                </div>

                <div className='flex flex-col'>
                  <label className="text-sm font-medium text-gray-100 mb-2">Trend Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData((p) => ({ ...p, title: e.target.value }))}
                    className="w-full bg-white/10 border border-white/30 focus:border-white rounded-md px-2 py-1 outline-none text-lg"
                    placeholder="e.g. AI Adoption in Enterprises"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-100 mb-2">Brief Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                    style={{width: "100%", height:"200px"}}
                    className="w-full bg-white/10 border border-white/30 focus:border-white rounded-md px-2 py-1 outline-none resize-none"
                    placeholder="Describe what this lifecycle trend represents..."
                  />
                </div>

                {/* Period Settings */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-sm font-medium text-gray-100 mb-2">Period Type</label>
                    <select
                      value={formData.periodType}
                      onChange={(e) => setFormData((p) => ({ ...p, periodType: e.target.value }))}
                      className="w-full bg-black border border-white/30 focus:border-white rounded-md px-2 py-2 h-10 outline-none"
                    >
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Year wise">Year wise</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-100 mb-2">Interval</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.interval}
                      onChange={(e) => setFormData((p) => ({ ...p, interval: parseInt(e.target.value) || 1 }))}
                      className="w-full bg-black border border-white/30 focus:border-white rounded-md px-2 py-2 outline-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className='flex flex-col'>
                    <label className="text-sm font-medium text-gray-100 mb-2">Starting Period</label>
                    <input
                      type="text"
                      value={formData.startPeriod}
                      onChange={(e) => setFormData((p) => ({ ...p, startPeriod: e.target.value }))}
                      placeholder={formData.periodType === 'Year wise' ? '2020' : '2020-01'}
                      className="w-full bg-white/10 border border-white/30 focus:border-white rounded-md px-2 py-2 outline-none"
                    />
                  </div>
                  <div className='flex flex-col'>
                    <label className="text-sm font-medium text-gray-100 mb-2">Ending Period</label>
                    <input
                      type="text"
                      value={formData.endPeriod}
                      onChange={(e) => setFormData((p) => ({ ...p, endPeriod: e.target.value }))}
                      placeholder={formData.periodType === 'Year wise' ? '2025' : '2025-12'}
                      className="w-full bg-white/10 border border-white/30 focus:border-white rounded-md px-2 py-2 outline-none"
                    />
                  </div>
                </div>

                <button
                  onClick={handleGenerateTemplate}
                  className="w-40 lg:w-64 text-xs lg:text-sm bg-emerald-500 hover:bg-emerald-600 py-2 rounded-md font-bold  transition-colors flex items-center justify-center gap-2"
                >
                  <span>Generate Periods Template</span>
                </button>
              </div>

              {/* Right Column - Scores Input */}
              <div>
                <label className="text-sm font-medium text-gray-100 mb-3">
                  Scores (1-100) for each period
                </label>
                <textarea
                  value={formData.scoresText}
                  onChange={(e) => setFormData((p) => ({ ...p, scoresText: e.target.value }))}
                  rows={12}
                  className="w-full font-mono bg-black border border-white/30 focus:border-white rounded-md p-2 text-emerald-300 resize-none outline-none"
                  placeholder='"2020":20, "2021":35, "2022":45 ...'
                />
                <p className="text-xs text-white/40 mt-2 flex items-center gap-1">
                  <span className="bg-white/10 px-2 py-0.5 rounded">Tip:</span>
                  Keys must be quoted. Values 1–100 only.
                </p>

                <button
                  onClick={() => handleSubmit()}
                  className="mt-2 w-48 lg:w-64 bg-white text-black py-2 rounded-md font-bold text-xs lg:text-sm hover:bg-gray-200 transition-colors flex items-center justify-center gap-3 shadow-xl shadow-white/20"
                >
                  <span>Submit &amp; Generate Graph</span>
                  <span className="text-sm lg:text-lg">→</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* GRAPH SECTION - appears after successful submit */}
        {showGraph && graphData && currentData && (
          <div id="graph-section" ref={previewRef} className="mt-16">
            <div className="bg-white/5 border border-white/10 rounded-md p-4">
              {/* Chart */}
              <div className="h-96 md:h-[480px]">
                <Line data={graphData} options={chartOptions} />
              </div>

              {/* Data Summary */}
              <div className="mt-2 grid grid-cols-1lg:grid-cols-3 gap-2 text-sm place-items-center">
                <div>
                  <span className="text-white/60">Title</span>
                  <span className="font-semibold text-lg">{currentData.title}</span>
                </div>
                <div>
                  <span className="text-white/60">Period</span>
                  <span className="font-semibold text-lg">
                    {currentData.startPeriod} — {currentData.endPeriod} ({currentData.periodType})
                  </span>
                </div>
                <div>
                  <span className="text-white/60">Average Score</span>
                  <span className="font-semibold text-lg">
                    {Math.round(
                      Object.values(currentData.scores).reduce((a, b) => a + b, 0) /
                        Object.keys(currentData.scores).length
                    )}
                    /100
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-1 place-items-center">
                <button
                  onClick={() => setShowAI(true)}
                  className="w-40 lg:w-40 text-xs lg:text-sm flex-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white px-2 py-2 rounded-md font-bold hover:brightness-110 transition-all flex items-center justify-center gap-2"
                >
                  ✨ AI Insights
                </button>

                <button
                  onClick={handleShare}
                  className="w-32 lg:w-40 text-xs lg:text-sm flex-1 bg-gradient-to-r from-blue-500 to-teal-500 border border-white/30 hover:border-white px-2 py-2 rounded-md font-bold transition-all"
                >
                  🔗 Share
                </button>

                <button
                  onClick={handleDownloadPDF}
                  className="w-40 lg:w-40 text-xs lg:text-sm flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 border border-white/30 hover:border-white px-2 py-2 rounded-md font-bold transition-all"
                >
                  ⬇️ Download PDF
                </button>

                <button
                //   onClick={() => alert('✅ Saved to backend (placeholder - implement your API here)')}
                  className="w-32 lg:w-40 text-xs lg:text-sm flex-1 bg-gradient-to-r from-teal-500 to-violet-500 bg-emerald-600 hover:bg-emerald-500 px-2 py-2 rounded-md font-bold transition-all"
                >
                  💾 Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Insights Modal */}
      {showAI && currentData && (
        <MyAIInsight data={currentData} onClose={() => setShowAI(false)} />
      )}

      {/* Footer hint */}
        <footer className="bg-black border-t border-blue-400 text-white py-4">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">© 2026 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-teal-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MyLifeTrends;